import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, RefreshCw, Save } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Rebuilt admin notifications page with:
 *  - Template editor
 *  - Email + SMS logs
 *  - Twilio SMS config + test-send
 */
export default function AdminNotificationsPage() {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).some((r) => r === "administrator" || r === "super_administrator");
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState({});
  const [emailLogs, setEmailLogs] = useState([]);
  const [smsLogs, setSmsLogs] = useState([]);
  const [smsCfg, setSmsCfg] = useState(null);
  const [testPhone, setTestPhone] = useState("");
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    const [t, e, s, c] = await Promise.all([
      api.get("/admin/notifications/templates"),
      api.get("/admin/notifications/logs/email"),
      api.get("/admin/notifications/logs/sms"),
      api.get("/admin/notifications/sms/config"),
    ]);
    setTemplates(t.data || []);
    setEmailLogs(e.data || []);
    setSmsLogs(s.data || []);
    setSmsCfg(c.data);
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  if (!isAdmin) return <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center max-w-lg">Admins only.</div>;

  const startEdit = (t) => setEditing((prev) => ({ ...prev, [t.id]: { title: t.title, body: t.body, email_subject: t.email_subject, active: t.active } }));
  const change = (id, k, v) => setEditing((p) => ({ ...p, [id]: { ...p[id], [k]: v } }));
  const save = async (t) => {
    try {
      await api.put(`/admin/notifications/templates/${t.id}`, { event: t.event, channel: t.channel, ...editing[t.id] });
      toast.success("Template updated");
      const nx = { ...editing }; delete nx[t.id]; setEditing(nx);
      load();
    } catch (err) { toast.error(err?.response?.data?.error || "Save failed"); }
  };
  const runTestSms = async () => {
    if (!testPhone) { toast.error("Enter a phone number"); return; }
    try {
      const { data } = await api.post("/admin/notifications/sms/test", { to_phone: testPhone });
      setTestResult(data);
      if (data.result.status === "sent") toast.success("SMS accepted by Twilio");
      else toast.warning(`Simulated: ${data.result.reason || ""}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.error || "Test failed"); }
  };

  return (
    <div className="max-w-6xl" data-testid="admin-notifications">
      <div className="mb-6">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Admin · Notifications</div>
        <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Notifications & channels</h1>
      </div>

      {/* SMS provider status */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5 mb-6" data-testid="sms-status">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-display font-semibold text-[#071b22]">SMS provider · Twilio</div>
            <div className="text-xs text-[#252A34]/70 mt-1">Status: <b className={smsCfg?.ok ? "text-emerald-700" : "text-amber-700"}>{smsCfg?.ok ? "ready" : "fallback simulated"}</b></div>
            {!smsCfg?.ok && (
              <div className="mt-2 text-xs text-[#252A34]">Missing: <b>{(smsCfg?.missing || []).join(", ")}</b>. Add to <code>backend/.env</code> and restart backend.</div>
            )}
            <ul className="mt-2 text-xs grid gap-1">
              {Object.entries(smsCfg?.details || {}).map(([k, v]) => (
                <li key={k}><span className="text-[#252A34]/60">{k}</span>: <b className={v ? "text-emerald-700" : "text-rose-600"}>{v ? "set" : "missing"}</b></li>
              ))}
            </ul>
          </div>
          <div className="flex items-center gap-2">
            <input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="+15555550100" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="sms-test-phone" />
            <button onClick={runTestSms} className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium" data-testid="sms-test-btn">
              <Send className="h-4 w-4" /> Send test
            </button>
          </div>
        </div>
        {testResult && (
          <div className="mt-3 p-3 rounded-xl bg-slate-50 border border-slate-100 text-xs">
            <b>Last test result:</b> {testResult.result.status} · provider={testResult.result.provider}{testResult.result.sid ? ` · sid=${testResult.result.sid}` : ""}{testResult.result.reason ? ` · reason=${testResult.result.reason}` : ""}
          </div>
        )}
      </section>

      {/* Template editor */}
      <section className="mb-8">
        <h2 className="font-display text-lg font-semibold text-[#071b22] mb-3">Templates ({templates.length})</h2>
        <div className="space-y-3">
          {templates.slice(0, 30).map((t) => {
            const draft = editing[t.id];
            return (
              <div key={t.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`admin-template-${t.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-xs text-[#252A34]/80">{t.event} · <b>{t.channel}</b></div>
                    <div className="text-sm text-[#071b22] mt-1"><b>Title:</b> {draft ? <input value={draft.title || ""} onChange={(e) => change(t.id, "title", e.target.value)} className="rounded border border-slate-200 px-2 py-1 text-sm w-64" data-testid={`tmpl-title-${t.id}`} /> : t.title}</div>
                    <div className="text-sm text-[#071b22] mt-1"><b>Body:</b> {draft ? <textarea value={draft.body || ""} onChange={(e) => change(t.id, "body", e.target.value)} className="rounded border border-slate-200 px-2 py-1 text-sm w-full" rows={2} data-testid={`tmpl-body-${t.id}`} /> : t.body}</div>
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    {draft ? (
                      <button onClick={() => save(t)} className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-4 py-1.5 text-xs font-medium" data-testid={`tmpl-save-${t.id}`}><Save className="h-3 w-3" /> Save</button>
                    ) : (
                      <button onClick={() => startEdit(t)} className="rounded-full border border-slate-200 px-4 py-1.5 text-xs font-medium" data-testid={`tmpl-edit-${t.id}`}>Edit</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Logs */}
      <div className="grid gap-6 lg:grid-cols-2">
        <LogPanel title="Email log" items={emailLogs} testid="email-logs" />
        <LogPanel title="SMS log" items={smsLogs} testid="sms-logs" />
      </div>
    </div>
  );
}

function LogPanel({ title, items, testid }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="font-display font-semibold text-[#071b22] mb-3">{title}</div>
      {items.length === 0 ? <div className="text-sm text-[#252A34]/60">No entries.</div> : (
        <ul className="divide-y text-xs">
          {items.slice(0, 30).map((r) => (
            <li key={r.id} className="py-2">
              <div className="text-[#071b22] truncate"><b>{r.subject || r.body?.slice(0, 60) || "—"}</b></div>
              <div className="text-[#252A34]/60">{r.to_email || r.to_phone} · {r.provider || "?"} · {r.status} · {r.created_at ? new Date(r.created_at).toLocaleString() : ""}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
